// @flow
import React, {useContext, useEffect, useState} from "react";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import PropTypes from "prop-types";
import GuiUtils from "../GUI_COMMON/SUPPORT/GuiUtils";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";

const SamplesList = ({name, samples, sampleType, onSampleClicked, onRepairClicked, onSampleCheckChanged, onOrderClicked, 
                         showCheckBox, showDelete, onDeleteClicked, readOnly, allowCheckIfOut, allowCheckIfOffPremises,
                         isLoading, thisOrderId=undefined, showImages,
                     returnNullOnNoneFound, profiles, newlyAddedSampleIds}) => {

    const [globalState] = useContext(StoreContext);
    const [filteredSamples, setFilteredSamples] = useState([]); // holds the samples filtered by sampleType

    useEffect(() => {
        const sList = samples?.filter(item => item.sampleType === sampleType)??[];
        // let's sort by sample name
        sList.sort((a,b) => a.sampleNumber?.localeCompare(b.sampleNumber, undefined, {numeric: true}));
        setFilteredSamples(sList);
    }, [samples]); // eslint-disable-line react-hooks/exhaustive-deps
    
    function markManuallyAddedSample(sampleItem) {
        const retVal =ArchiverGuiUtils.ShowSampleIsManuallyEdited(sampleItem);
        if (retVal) {
            return  <span className="ml-1">{retVal}</span>
        }
        return null;
    }

    function sampleIsOut(item):boolean {
        return(!item.magBarcode || item.magBarcode === "");
    }
    
    function dumpRepairToSelect(itemId) {

        if (readOnly) { // no click to repair
            return (<span className="align-self-baseline">
                    <div className="mx-2 badge li-fg-white li-bg-danger">attention</div>
                </span>);
        }

        return (<span className="align-self-baseline">
                    <div className="mx-2 badge li-fg-white li-bg-danger li-pointer"
                         data-toggle="tooltip" data-placement="auto" title="Click to Repair"
                         onClick={() => onRepairClicked(itemId)}>
                            attention
                            <i className="fa fa-cog ml-2"/>
                    </div>
            {dumpRepairHelp()}
                </span>);
    }

    function dumpRepairHelp() {
        if (showCheckBox) {
            return (<>
                <i className="fa fa-arrow-left mr-2"/>
                <span className="li-font-small"> repair to select</span>
            </>);
        }
        return null;
    }

    function handleDeleteClicked(item) {
        if (onDeleteClicked) {
            onDeleteClicked(item.id);
        }
    }
    
    function handleMouseOverImage(e,sampleItem, sampleImageItemId) {
        console.debug(`over image at ${e.clientX},${e.clientY}`);
        const magnifier = document.getElementById("imageMagnifier-"+name);
        
        const targetOffsetTop = e.target.offsetTop;
        const targetOffsetLeft = e.target.offsetLeft;
        
        //magnifier.style.top=(e.pageY-100)+"px";
        //magnifier.style.left=(e.pageX+50)+"px";
        magnifier.style.top=(targetOffsetTop-100)+"px";
        magnifier.style.left=(targetOffsetLeft+50)+"px";

        const magnifierBarcode = document.getElementById("magnifierBarcode-"+name);
        magnifierBarcode.innerText=LIUtils.SanitizeBarcodeToLength(sampleItem.barcode);
        
        const magnifierImg = document.getElementById("magnifierImg-"+name);
        if(sampleItem.imageData) {
            magnifierImg.src="data:image/png;base64,"+sampleItem.imageData;
            magnifier?.classList.remove("hide");
            magnifier?.classList.add("show");

            const imageDiv = document.getElementById(sampleImageItemId);
            if(imageDiv) {
                // imageDiv.style.borderStyle="inset";
                // imageDiv.style.borderWidth="5px";
                // imageDiv.style.borderColor="var(--li-main-color)";
                imageDiv.style.outlineStyle="solid";
                imageDiv.style.outlineWidth="5px";
                imageDiv.style.outlineColor="yellow";
            }
        }
        else {
            magnifierImg.src="";  // bug fix - 231129 -pott
        }
    }
    
    function handleMouseOverImageOut(sampleImageItemId) {
        console.debug("over image out");
        const magnifier = document.getElementById("imageMagnifier-"+name)
        magnifier?.classList.remove("show");
        magnifier?.classList.add("hide");
        const magnifierImg = document.getElementById("magnifierImg-"+name);
        magnifierImg.src="";  // bug fix - 231129 -pott

        const imageDiv = document.getElementById(sampleImageItemId);
        if(imageDiv) {
            imageDiv.style.outlineStyle="none";
        }
    }
    
    function dumpImageMagnifier() {
        
        return(
            <div id={"imageMagnifier-"+name} className="li-magnifier" style={{position:"absolute",top:"auto"}}>
                <div id={"magnifierBarcode-"+name} className="text-center w-100"/>
                <img id={"magnifierImg-"+name} className="magnifierImg" alt="missing sample pic"/>
            </div>
        );
    }

    function dumpSampleImageOrIcon(imageData, sType, sampleImageItemId) {
        if(imageData) {
            return LIUtils.getImgFromImageData(imageData, null, 50);
        }
        const sTypeImage = GuiUtils.GetSampleTypeImageGivenId(sType, globalState.sampleTypes.items);
        if(sTypeImage) {
            return LIUtils.getImgFromImageData(sTypeImage, null, 50, sampleImageItemId);
        }
        return null;
    }

    function shouldDisableCheckBox(item, needsAttention) {
        if(readOnly || needsAttention) {
            return true; // disabled checkbox
        }
        if(!allowCheckIfOut && sampleIsOut(item)) {
            return true; // disabled checkbox
        }
        if(!allowCheckIfOffPremises && item.isOffPremises) {
            return true; // disabled checkbox
        }
        return false;  // can show the checkbox
    }
    
    function dumpSampleRow(item) {
        const sampleStr = GuiUtils.BuildSampleDetailsStr(item.barcode, item.caseNumber,item.sampleNumber);
        let needsAttention=!LIUtils.SampleHasCriticalInfo(item.caseNumber,item.sampleNumber,item.profileId);
        if (needsAttention && LIUtils.SampleIsNoCaseSampleRequired(item, profiles)) { // no case sample required - override so we can show the checkbox
            needsAttention = false;
        }

        const sampleImageItemId = "sampleImage" + item.id.toString();
        
        const isNewlyAdded = newlyAddedSampleIds && newlyAddedSampleIds.has(item.id);
        const animationClass = isNewlyAdded ? ' li-box-item-flash' : '';
        
        return (
            <div key={item.id} className={`row p-0${animationClass}`} style={{marginBottom:"4px"}}>
                {showCheckBox &&
                <input type="checkbox" className="m-2 mr-1" name={name} data-parent-id={item.id} aria-label="Checkbox"
                       disabled={shouldDisableCheckBox(item, needsAttention)}
                       onChange={(e) => onSampleCheckChanged(item,e.target.checked)}/>
                }
                <div className="align-self-center text-nowrap">
                    {showDelete && !readOnly &&
                        <span className="m-2 mr-1 li-pointer" data-toggle="tooltip" title="Delete this sample"
                              onClick={() => handleDeleteClicked(item)}>
                                <i className="fa fa-lg fa-times-circle li-fg-danger"/></span>
                    }
                    <div className="d-inline-block">
                        <div className="p-0 small mr-1 d-inline-block" 
                             onMouseOver={(e)=>handleMouseOverImage(e,item, sampleImageItemId)}
                                onMouseOut={() => handleMouseOverImageOut(sampleImageItemId)}>
                            {dumpSampleImageOrIcon(item.imageData,item.sampleType,sampleImageItemId)}
                        </div>
                        <span className="li-pointer btn-link" onClick={() => onSampleClicked(item.id)}>{LIUtils.SanitizeBarcodeToLength(sampleStr)}</span>
                    </div>
                    {ArchiverGuiUtils.ShowSampleIsInOutOrPendingOrPicked(item,onOrderClicked,true,thisOrderId)}
                    {markManuallyAddedSample(item)}
                    {/* avoid showing the attention icon if the sample is Off Premises */}
                    {!item.isOffPremises && needsAttention && dumpRepairToSelect(item.id)}
                </div>
            </div>
        );
    }

    if (isLoading) {
        return <LoadingSpinner medium center/>;
    }
    
    if (!filteredSamples || filteredSamples.length === 0) {
        if(returnNullOnNoneFound) return null;
        
        const sampleTypeName =GuiUtils.GetSampleTypeNameGivenId(sampleType,globalState.sampleTypes.items);
        return <div className="ml-5">no {sampleTypeName} found</div>;
    }
    
    return (
        <div className="row row-cols-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2 m-0 p-0 position-static">
            {filteredSamples.map((item) => dumpSampleRow(item))}
            {dumpImageMagnifier()}
        </div>
    );
}

/* this is used by external queriers if they need to determine if anything 
       will be returned by this component or not (### kind of hacky)
       Returns: null if nothing will be returned, otherwise the filtered samples for the given sample type
     */
export function SampleListWillReturnNothing(samples:[], sampleType:number) {
    const filteredSamples = samples?.filter(item => item.sampleType === sampleType);
    return filteredSamples?.length === 0;
}

SamplesList.propTypes = {
    name: PropTypes.string.isRequired,
    onSampleClicked: PropTypes.func,
    onSampleCheckChanged: PropTypes.func,
    onOrderClicked: PropTypes.func,
    samples: PropTypes.array,
    sampleType: PropTypes.number.isRequired,
    showCheckBox: PropTypes.bool,
    isLoading: PropTypes.bool,
    showDelete: PropTypes.bool,
    onDeleteClicked: PropTypes.func,
    readOnly: PropTypes.bool,
    allowCheckIfOut: PropTypes.bool,
    allowCheckIfOffPremises: PropTypes.bool,
    onRepairClicked: PropTypes.func,
    thisOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showImages: PropTypes.bool,
    returnNullOnNoneFound: PropTypes.bool,
    profiles: PropTypes.array,
    newlyAddedSampleIds: PropTypes.object
}

export default SamplesList;